// ABOUTME: Read-only section renderer shared by SharedReportPage and ReportPrintPage.
// ABOUTME: Renders heading, text, visualization, table, and insight_reference sections without editing controls.

import DOMPurify from 'dompurify';
import { ReportChart } from './ReportChart';
import { InsightRenderer } from '@/components/investigation/InsightRenderer';
import { normalizeChartData, resolveChartType } from './utils/chart-data';
import type { ReportSection } from '@/types/api';

/**
 * Check if chart data is multi-series (e.g., {hour: "12am", Monday: 63, Tuesday: 62})
 * vs simple single-series ({label: "x", value: 5}).
 */
function isMultiSeriesData(data: any[]): boolean {
  if (!data || data.length === 0) return false;
  const first = data[0];
  return !('label' in first && 'value' in first);
}

interface ReadOnlySectionProps {
  section: ReportSection;
  chartHeight?: number;
  maxTableRows?: number;
  showChartLegend?: boolean;
  /** Wrapper for data sections (visualization, table) — used by SharedReportPage for CTA overlay */
  wrapDataSection?: (sectionId: string, children: React.ReactNode) => React.ReactNode;
}

/**
 * Renders a single report section in read-only mode. Uses the same CSS
 * classes as the report editor (`.report-editor .tiptap`) so that
 * typography and spacing are visually identical.
 */
export function ReadOnlySection({
  section,
  chartHeight = 300,
  maxTableRows = 50,
  showChartLegend = true,
  wrapDataSection,
}: ReadOnlySectionProps) {
  if (section.type === 'heading') {
    return (
      <div
        className="tiptap"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content || '') }}
      />
    );
  }

  if (section.type === 'text') {
    return (
      <div
        className="tiptap"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content || '') }}
      />
    );
  }

  if (section.type === 'visualization') {
    const viz = section.visualization;
    if (!viz) {
      return (
        <div className="rounded-sm bg-surface-50 border border-surface-200 p-6 text-center">
          <p className="text-sm text-surface-500">Visualization data unavailable</p>
        </div>
      );
    }

    const vizData = viz.data || [];
    const chartType = resolveChartType(viz);

    let chartContent: React.ReactNode;

    if (isMultiSeriesData(vizData)) {
      // Multi-series data (from insights): pass raw data to ReportChart
      chartContent = (
        <ReportChart
          rawData={vizData}
          type={chartType}
          height={viz.height || chartHeight}
          showLegend={showChartLegend}
        />
      );
    } else {
      // Simple {label, value} data
      const chartData = normalizeChartData(viz);
      chartContent = (
        <ReportChart
          data={chartData}
          type={chartType}
          height={viz.height || chartHeight}
          showLegend={showChartLegend}
        />
      );
    }

    // TODO(2026-03-04, claude): The inline table rendering below duplicates the
    // table rendering in the section.type === 'table' branch further down this file.
    // Extract a shared InlineTableRenderer component to avoid drift.
    // Convert backend table_data format to inline table for chart-table insights
    const tableDataForViz = section.table_data;
    const hasTable = tableDataForViz && tableDataForViz.columns && tableDataForViz.rows;

    const chartElement = (
      <div>
        <div className="rounded-sm bg-surface-0 border border-surface-200 p-4 overflow-hidden">
          {viz.title && (
            <h4 className="text-sm font-medium text-surface-700 mb-3">{viz.title}</h4>
          )}
          {chartContent}
        </div>
        {hasTable && (() => {
          const columns = tableDataForViz.columns || [];
          const rows = tableDataForViz.rows || [];
          const displayLimit = tableDataForViz.max_display_rows || maxTableRows;
          return (
            <div className="mt-4 rounded-sm border border-surface-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      {columns.map((col: any, i: number) => (
                        <th key={i}>
                          {typeof col === 'object'
                            ? col.header || col.name || col.key || col.id
                            : col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, displayLimit).map((row: any, rowIndex: number) => (
                      <tr key={rowIndex}>
                        {columns.map((col: any, colIndex: number) => {
                          const key =
                            typeof col === 'object'
                              ? col.key || col.id || col.name || col.header
                              : col;
                          return <td key={colIndex}>{row[key] ?? ''}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > displayLimit && (
                <div className="px-4 py-2 border-t border-surface-200 bg-surface-50 text-center">
                  <p className="text-xs text-surface-400">
                    Showing {displayLimit} of {rows.length} rows
                  </p>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );

    return wrapDataSection ? wrapDataSection(section.id, chartElement) : chartElement;
  }

  if (section.type === 'table') {
    const tableData = section.table_data;
    if (!tableData) {
      return (
        <div className="rounded-sm bg-surface-50 border border-surface-200 p-6 text-center">
          <p className="text-sm text-surface-500">Table data unavailable</p>
        </div>
      );
    }

    const columns = tableData.columns || [];
    const rows = tableData.rows || [];
    const title = tableData.title;
    const displayLimit = tableData.max_display_rows || maxTableRows;

    const tableElement = (
      <div className="rounded-sm border border-surface-200 overflow-hidden">
        {title && (
          <div className="px-4 py-3 bg-surface-50 border-b border-surface-200">
            <h4 className="text-sm font-medium text-surface-700">{title}</h4>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((col: any, i: number) => (
                  <th key={i}>
                    {typeof col === 'object'
                      ? col.header || col.name || col.key || col.id
                      : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, displayLimit).map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {columns.map((col: any, colIndex: number) => {
                    const key =
                      typeof col === 'object'
                        ? col.key || col.id || col.name || col.header
                        : col;
                    return <td key={colIndex}>{row[key] ?? ''}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > displayLimit && (
          <div className="px-4 py-2 border-t border-surface-200 bg-surface-50 text-center">
            <p className="text-xs text-surface-400">
              Showing {displayLimit} of {rows.length} rows
            </p>
          </div>
        )}
      </div>
    );

    return wrapDataSection ? wrapDataSection(section.id, tableElement) : tableElement;
  }

  if (section.type === 'insight_reference') {
    // Prefer structured_insight — extensible rendering for all insight types
    if (section.structured_insight) {
      const insightElement = (
        <InsightRenderer
          insight={section.structured_insight}
          minimal={true}
          showSmartActions={false}
        />
      );
      return wrapDataSection ? wrapDataSection(section.id, insightElement) : insightElement;
    }

    // Legacy fallbacks (inline data — no API call needed, works for unauthenticated viewers)
    if (section.visualization) {
      return (
        <ReadOnlySection
          section={{ ...section, type: 'visualization' }}
          chartHeight={chartHeight}
          maxTableRows={maxTableRows}
          showChartLegend={showChartLegend}
          wrapDataSection={wrapDataSection}
        />
      );
    }
    if (section.table_data) {
      return (
        <ReadOnlySection
          section={{ ...section, type: 'table' }}
          chartHeight={chartHeight}
          maxTableRows={maxTableRows}
          showChartLegend={showChartLegend}
          wrapDataSection={wrapDataSection}
        />
      );
    }
    if (section.content) {
      return (
        <div
          className="tiptap"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(section.content) }}
        />
      );
    }

    // Last resort: if insight_ref exists but no inline data, show placeholder
    // (InsightReferenceSection requires auth, so we can't use it in public viewer)
    return (
      <div className="rounded-sm bg-surface-50 border border-surface-200 p-6 text-center">
        <p className="text-sm text-surface-500">Referenced content unavailable</p>
      </div>
    );
  }

  return null;
}
